import { ApolloError, isApolloError } from '@apollo/client';
import { GraphQLError } from 'graphql';

/**
 Based on ../../../../lessonup-2.0/packages/internal/graphql-shared/src/shared.errors.ts
 Since we don't have access to the graphql-shared package, we need to copy the error codes here.
*/
const list = [
  'INTERNAL_SERVER_ERROR',
  'INVALID_PARAMS',
  'FEATURE_DISABLED',
  'FORBIDDEN',
  'UNAUTHORIZED',
  'ENTITY_NOT_FOUND',
  'NOT_SUPPORTED',
  'EXTERNAL_SERVICE_UNAVAILABLE',
  'NO_TEXT_FOUND_BY_OCR',
] as const;
export type GraphQLErrorCode = (typeof list)[number];

/**
 * Extracts the first GraphQLErrorCode from an ApolloError.
 * Returns undefined if the error is not an ApolloError or if the code is unknown.
 */
export const graphQLErrorCodeFromError = (error: unknown): GraphQLErrorCode | undefined => {
  const firstCode = firstGraphQLErrorCode(error) as GraphQLErrorCode | undefined;
  if (firstCode && list.includes(firstCode)) return firstCode;
};

export const firstGraphQLError = (error: unknown): GraphQLError | undefined => {
  return isValidApolloError(error) ? error.graphQLErrors[0] : undefined;
};

/**
 * Extracts the first GraphQLErrorCode from an ApolloError as a string.
 * Returns undefined if the error is not an ApolloError
 */
export const firstGraphQLErrorCode = (error: unknown): string | undefined => {
  return ((isValidApolloError(error) && firstGraphQLError(error)?.extensions.code) as string) || undefined;
};

/**
 * Extracts the extensions from the first GraphQLError in an ApolloError.
 * Returns undefined if the error is not an ApolloError.
 */
export const firstGraphQLErrorExtensions = (error: unknown): Record<string, unknown> | undefined => {
  return isValidApolloError(error) ? firstGraphQLError(error)?.extensions : undefined;
};

export const graphQLErrorMessage = (error: unknown): string | undefined => {
  return (isValidApolloError(error) && firstGraphQLError(error)?.message) || undefined;
};

const isValidApolloError = (error: unknown): error is ApolloError => {
  return error instanceof Error && isApolloError(error);
};
